import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { StickyNote } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAccountNotes } from '../hooks/useAccountNotes';

const CATEGORY_STYLES: Record<string, string> = {
  general: 'bg-gray-100 text-gray-700',
  follow_up: 'bg-blue-100 text-blue-700',
  internal: 'bg-purple-100 text-purple-700',
  issue: 'bg-red-100 text-red-700',
};

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  follow_up: 'Follow-up',
  internal: 'Internal',
  issue: 'Issue',
};

interface AccountNotesCardProps {
  accountId: string;
  onNavigateToTab: (tab: string) => void;
}

export default function AccountNotesCard({ accountId, onNavigateToTab }: AccountNotesCardProps) {
  const { data, isLoading } = useAccountNotes(accountId, { pageLength: 3 });

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-4 w-4" />
            Recent Notes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </CardContent>
      </Card>
    );
  }

  const notes = data?.notes || [];
  const total = data?.total || 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2">
          <StickyNote className="h-4 w-4" />
          Recent Notes
          {total > 0 && (
            <Badge variant="secondary" className="text-xs">
              {total}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>Latest account notes</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {notes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No notes yet
          </p>
        ) : (
          <>
            {notes.map((note) => (
              <div key={note.id} className="border rounded-md p-2.5 space-y-1">
                <p className="text-sm line-clamp-2" style={{ whiteSpace: 'pre-wrap' }}>
                  {note.content}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{note.created_by_name}</span>
                  {note.category && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0 ${CATEGORY_STYLES[note.category] || ''}`}
                    >
                      {CATEGORY_LABELS[note.category] || note.category}
                    </Badge>
                  )}
                  <span className="ml-auto">
                    {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>
            ))}
          </>
        )}

        <button
          onClick={() => onNavigateToTab('notes')}
          className="text-xs text-primary hover:underline"
        >
          View all notes &rarr;
        </button>
      </CardContent>
    </Card>
  );
}
